import React, { useEffect, useState, useCallback } from "react";
import AdminNav from "../components/Admin/AdminNav";
import { API_URL } from "../config";
import { authHeader, canManageProducts } from "../auth";
import styles from "./discounts.module.css";

const EMPTY_FORM = { code: "", type: "percent", value: "", label: "" };

async function readError(response, fallback) {
  const text = await response.text();
  try {
    return JSON.parse(text).message || fallback;
  } catch {
    return text || fallback;
  }
}

const describe = (d) =>
  d.type === "percent"
    ? `${d.value}% off the subtotal`
    : `₦${new Intl.NumberFormat("en-NG").format(d.value)} off the subtotal`;

const Discounts = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const canManage = canManageProducts();

  const fetchCodes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/discounts`, { headers: authHeader() });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not load discount codes."));
      }
      setCodes(await response.json());
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/discounts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ ...form, value: Number(form.value) }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not create the code."));
      }
      const created = await response.json();
      setForm(EMPTY_FORM);
      flash(`${created.code} created — shoppers can use it right away.`);
      fetchCodes();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (discount) => {
    setError("");
    try {
      const response = await fetch(`${API_URL}/discounts/${discount._id || discount.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ active: !discount.active }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not update the code."));
      }
      flash(`${discount.code} is now ${discount.active ? "disabled" : "active"}.`);
      fetchCodes();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (discount) => {
    if (!window.confirm(`Delete code ${discount.code}? Shoppers will no longer be able to use it.`)) {
      return;
    }
    setError("");
    try {
      const response = await fetch(`${API_URL}/discounts/${discount._id || discount.id}`, {
        method: "DELETE",
        headers: authHeader(),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not delete the code."));
      }
      flash(`${discount.code} deleted.`);
      fetchCodes();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className={styles.page}>
      <AdminNav />
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Backoffice</span>
          <h1 className={styles.title}>Discount Codes</h1>
          <p className={styles.subtitle}>
            Referral and promo codes shoppers can apply in the cart. Changes take
            effect immediately — no redeploy needed.
          </p>
        </header>

        {error && <p className={styles.error}>{error}</p>}
        {notice && <p className={styles.success}>{notice}</p>}

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Codes</h2>
          {loading ? (
            <p className={styles.loading}>Loading codes…</p>
          ) : codes.length === 0 ? (
            <p className={styles.loading}>No codes yet — create the first one below.</p>
          ) : (
            <ul className={styles.list}>
              {codes.map((discount) => (
                <li key={discount._id || discount.id} className={styles.row}>
                  <div className={styles.rowInfo}>
                    <span className={discount.active ? styles.code : styles.codeInactive}>
                      {discount.code}
                    </span>
                    <span className={styles.rowLabel}>{describe(discount)}</span>
                    <span className={styles.rowLabel}>
                      Used {discount.usedCount || 0}{" "}
                      {(discount.usedCount || 0) === 1 ? "time" : "times"}
                      {discount.createdBy && ` · created by ${discount.createdBy}`}
                    </span>
                  </div>
                  <span className={discount.active ? styles.statusChip : styles.statusChipOff}>
                    {discount.active ? "Active" : "Disabled"}
                  </span>
                  {canManage && (
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => toggleActive(discount)}
                      >
                        {discount.active ? "Disable" : "Enable"}
                      </button>
                      <button
                        type="button"
                        className={styles.dangerBtn}
                        onClick={() => handleDelete(discount)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {canManage && (
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Create a code</h2>
            <form onSubmit={handleCreate}>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="d-code">Code</label>
                  <input
                    id="d-code"
                    className={styles.input}
                    required
                    placeholder="FRIENDS10"
                    value={form.code}
                    onChange={(e) =>
                      setForm({ ...form, code: e.target.value.toUpperCase() })
                    }
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="d-type">Type</label>
                  <select
                    id="d-type"
                    className={styles.select}
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="percent">Percent off</option>
                    <option value="fixed">Fixed ₦ off</option>
                  </select>
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="d-value">
                    {form.type === "percent" ? "Percent (1–100)" : "Amount (₦)"}
                  </label>
                  <input
                    id="d-value"
                    className={styles.input}
                    type="number"
                    required
                    min="1"
                    max={form.type === "percent" ? 100 : undefined}
                    placeholder={form.type === "percent" ? "10" : "5000"}
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                  />
                </div>
              </div>
              <p className={styles.hint}>
                Shoppers type the code in the cart; it is checked against the store
                database, matched case-insensitively.
              </p>
              <button className={styles.submit} type="submit" disabled={saving}>
                {saving ? "Creating…" : "Create code"}
              </button>
            </form>
          </section>
        )}
      </div>
    </div>
  );
};

export default Discounts;
